import React from "react";
import { splitFormControlWrapperProps } from "@cmp-src/form/FormControlWrapper/helpers";
import { FormControlWrapper } from "@cmp-src/form/FormControlWrapper";
import { CalendarInputProps, CalendarInputPropsWithFormControl } from "./types";
import { Calendar } from "@v2/components/calendar/Calendar";
import { CalendarTrigger } from "@cmp-src/components/calendar/CalendarTrigger";
import { Calendar2Icon } from "@v2/icons";
import { Popover } from "@v2/components/overlay/Popover/Popover";
import { ModalButtons } from "@v2/components/overlay/ModalButtons/ModalButtons";
import { CalendarDatesList } from "@v2/components/calendar/CalendarDatesList";
import { CalendarProps, DateValue } from "@v2/components/calendar/types";
import { cn, useDisclosure } from "@gettactic/helpers";
import { Switch } from "@v2/components/ui/Switch";
import { ArrowsGroup } from "@cmp-src/components/ArrowsGroup/ArrowsGroup";
import {
  getNextDay,
  getPreviousDay
} from "@cmp-src/@v2/components/calendar/CalendarDayView/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";

export const CalendarInputInput = React.forwardRef<
  HTMLInputElement,
  Omit<CalendarInputProps, "mode"> &
    Partial<Pick<CalendarInputProps, "mode">> & {
      onSelect?: (date: DateValue | undefined) => void;
    } & {
      withArrows?: boolean;
    }
>((props, ref) => {
  const {
    inputClassName,
    className,
    mode: selectedMode = "single",
    allowMultipleSwitch = false,
    selectedDate,
    setValueOnApply = false,
    onSelect,
    toYear: controlledToYear,
    withArrows = false,
    ...rest
  } = props;

  const toYear = controlledToYear ?? new Date().getFullYear() + 5;

  const pickerDisclosure = useDisclosure();
  const { isBreakpoint } = useBreakpoint();
  const [selectedDates, setSelectedDates] = React.useState<
    DateValue | undefined
  >(selectedDate);
  const [forceMultiDay, setForceMultiDay] = React.useState<boolean>(
    selectedMode === "multiple"
  );
  const isMd = isBreakpoint("md");

  const deleteDate = (dateIndex: number) => {
    if (Array.isArray(selectedDates))
      setSelectedDates(selectedDates.filter((_, index) => index !== dateIndex));
  };

  const handleSelectDate = (date: DateValue | undefined) => {
    if (!setValueOnApply) {
      onSelect?.(date);
      return;
    }
    setSelectedDates(date);
  };

  const handleOnApply = () => {
    onSelect?.(selectedDates);
    pickerDisclosure.close();
  };

  const handleCancel = () => {
    pickerDisclosure.close();
  };

  const handleMultiDaySwitch = (checked: boolean) => {
    setForceMultiDay(checked);

    // Make sure to keep at least one date selected when switching from multi-day to single-day mode
    if (checked && selectedMode === "single") {
      if (selectedDate instanceof Date) handleSelectDate([selectedDate]);
      return;
    }

    if (!checked && selectedMode === "single") {
      if (Array.isArray(selectedDate) && selectedDate[0])
        handleSelectDate(selectedDate[0]);
      return;
    }
  };

  const properties: Omit<CalendarProps, "mode"> & {
    onSelect: typeof handleSelectDate;
  } = {
    ...rest,
    toYear,
    required: false,
    className: cn("border-0", className),
    onSelect: handleSelectDate
  };

  const value = setValueOnApply ? selectedDates : selectedDate;
  const showMultiDaySwitch =
    allowMultipleSwitch /*&& selectedMode !== 'multiple'*/;
  const mode = forceMultiDay
    ? "multiple"
    : selectedMode === "multiple"
      ? "single"
      : selectedMode;

  let calendar = <></>;

  React.useEffect(() => {
    if (pickerDisclosure.isOpen) {
      setSelectedDates(selectedDate);
    }
  }, [pickerDisclosure.isOpen, selectedDate]);

  if (mode === "single") {
    calendar = (
      <Calendar
        mode={"single"}
        selected={value instanceof Date ? value : undefined}
        {...properties}
      />
    );
  }

  if (mode === "range") {
    calendar = (
      <Calendar
        mode={"range"}
        selected={
          value && !Array.isArray(value) && !(value instanceof Date)
            ? value
            : undefined
        }
        {...properties}
      />
    );
  }

  if (mode === "multiple") {
    calendar = (
      <Calendar
        mode={"multiple"}
        selected={Array.isArray(value) ? value : undefined}
        {...properties}
      />
    );
  }

  const handleNext =
    selectedDate instanceof Date
      ? () => {
          const tomorrow = getNextDay(selectedDate);
          onSelect?.(tomorrow);
        }
      : undefined;

  const handlePrev =
    selectedDate instanceof Date
      ? () => {
          const yesterday = getPreviousDay(selectedDate);
          onSelect?.(yesterday);
        }
      : undefined;

  return (
    <div className="flex w-full items-center md:gap-2 shadow-lg md:shadow-none">
      {withArrows && (
        <ArrowsGroup
          className="hover:shadow-none"
          leftProps={{
            onClick: handlePrev,
            className: "h-[42px]"
          }}
          showLeft={!isMd}
          showRight={false}
        />
      )}
      <Popover
        mainContainerClassName={""}
        content={
          <>
            {calendar}
            {showMultiDaySwitch && (
              <div className={"mt-2 select-none p-2.5"}>
                <Switch
                  variant={"squared"}
                  size={"xs"}
                  label={"Multi-Day"}
                  onCheckedChange={handleMultiDaySwitch}
                  checked={forceMultiDay}
                />
              </div>
            )}
          </>
        }
        footer={
          setValueOnApply ? (
            <ModalButtons onApply={handleOnApply} onCancel={handleCancel} />
          ) : undefined
        }
        sideContent={
          mode === "multiple" ? (
            <CalendarDatesList
              dates={Array.isArray(selectedDates) ? selectedDates : []}
              onDelete={deleteDate}
            />
          ) : undefined
        }
        onOpenChange={pickerDisclosure.onOpenChange}
        open={pickerDisclosure.isOpen}
      >
        <CalendarTrigger
          dates={selectedDate}
          icon={Calendar2Icon}
          className={cn(
            withArrows && "rounded-none",
            "w-full flex-1 text-[12px] md:rounded-lg md:text-[14px]"
          )}
        />
      </Popover>
      {withArrows && (
        <ArrowsGroup
          className="hover:shadow-none"
          rightProps={{
            onClick: handleNext,
            className: "h-[42px]"
          }}
          leftProps={{
            onClick: handlePrev,
            className: "h-[42px]"
          }}
          showLeft={isMd}
        />
      )}
    </div>
  );
});

CalendarInputInput.displayName = "CalendarInputInput";

export const CalendarInput = React.forwardRef<
  HTMLInputElement,
  CalendarInputPropsWithFormControl & {
    withArrows?: boolean;
  }
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <>
      <FormControlWrapper variant={"unstyled"} {...wrapperProps}>
        <CalendarInputInput
          ref={ref}
          {...inputProps}
          required={false}
          withArrows={props.withArrows}
        />
      </FormControlWrapper>
    </>
  );
});

CalendarInput.displayName = "CalendarInput";
